import React from 'react'
import ActionLink from './ActionLink'
import AddressAutocomplete from './AddressAutocomplete'
import AioText from './aio-inputs/AioText'
import {GridCol, GridRow, GridSection} from './grid-form'
import type { DraggableId, DraggableProvided, DraggableProvidedDraggableProps, DraggableProvidedDragHandleProps, DraggableRubric, DraggableStateSnapshot} from '@hello-pangea/dnd';
import {Draggable} from '@hello-pangea/dnd'
import {TestId} from '../contexts/testid'
import styles from './Stop.less'

// require('../../stylesheets/googlePlacesAutocomplete');
import _ from 'lodash'

import update from 'immutability-helper2'

import setIn from 'lodash/fp/set'
import AioSelect from './aio-inputs/AioSelect'
import {COUNTRY_LIST} from '../data/countries'

export type BookingStopAddress = {
    place_name: string
    street: string
    city: string
    postal_code: string
    province: string
    country: string
}

type StopState =  {
    splitAddress: boolean
    address: string
} & BookingStopAddress

export type StopPosition = 'PU' | 'ST' | 'DO'

interface StopProps {
    value: AnyDuringMigration
    number: any
    letter: string
    addStop: AnyFn
    removeStop: AnyFn
    position: StopPosition
    onChange: AnyFn
    googleMapsApiKey: string
    dragHandleProps?: DraggableProvidedDragHandleProps
    draggableProps?: DraggableProvidedDraggableProps
    innerRef?: React.Ref<HTMLDivElement>
    isDragging?: boolean
    testId?: string
}

export const STOP_COLORS = {
    PU: 'var(--clr-vib-stop-pickup,#19bc94)',
    ST: 'var(--clr-vib-stop-stop,#e29e4b)',
    DO: 'var(--clr-vib-stop-dropoff,#d45769)',
} as const

export function formatBookingStopAddress(value: BookingStopAddress) {
    let address = value.street ? value.street.trim() : ''
    if(value.place_name) {
        if(address) {
            address += ` (${value.place_name})`
        } else {
            address = value.place_name
        }
    }

    // console.log(JSON.stringify([address, value.city, value.province, value.postal_code]));

    return [address, value.city, value.province, value.postal_code].filter(s => s).join(', ')
}

export default class Stop extends React.Component<StopProps, StopState> {

    constructor(props: StopProps) {
        super(props)
        this.state = {
            splitAddress: false,
            address: this.getAddress(),
            place_name: '',
            street: '',
            city: '',
            postal_code: '',
            province: '',
            country: '',
        }
    }

    getAddress = () => {
        return formatBookingStopAddress(this.props.value)
    }

    render() {
        const {number, letter, addStop, removeStop, value, position, dragHandleProps, draggableProps, innerRef, testId, onChange} = this.props
        const key = value.key

        const color = STOP_COLORS[position]

        const autocompleteLabel = {
            PU: "Address (Pick-up)",
            ST: "Address (Stop)",
            DO: "Address (Drop-off)",
        }[position]

        return (
            <TestId prefix={testId || `stop-${letter}`}>
                <GridRow ref={innerRef} {...draggableProps}>
                    <GridCol shrink center style={{backgroundColor: '#fff', borderRadius: '8px', border: `2px solid ${color}`, color}} className={styles.dragHandle} {...dragHandleProps}>
                        <span className="section-number">
                            <span className="segment-number">{number}</span>
                            <span className="stop-letter">{letter}</span>
                        </span>
                    </GridCol>

                    {this.state.splitAddress ?
                        <GridCol relative>
                            <GridSection>
                                <GridRow>
                                    <GridCol minWidth={180}>
                                        <AioText label="Place Name" testId="place-name" {...this.linkState('place_name')}/>
                                    </GridCol>
                                    <GridCol minWidth={180}>
                                        <AioText required label="Street Address" testId="street" {...this.linkState('street')}/>
                                    </GridCol>
                                    <GridCol minWidth={180}>
                                        <AioText required label="City" testId="city" {...this.linkState('city')}/>
                                    </GridCol>
                                </GridRow>
                                <GridRow>
                                    <GridCol minWidth={180}>
                                        <AioText required label="State/Province" testId="state" {...this.linkState('province')}/>
                                    </GridCol>
                                    <GridCol minWidth={180}>
                                        <AioSelect required placeholder="— Not Selected —" label="Country" testId="country" {...this.linkState('country')} options={COUNTRY_LIST}/>
                                    </GridCol>
                                    <GridCol minWidth={180}>
                                        <AioText label="Zip/Postal Code" testId="zip" {...this.linkState('postal_code')}/>
                                    </GridCol>
                                </GridRow>
                            </GridSection>
                            <ActionLink className={styles.autocompleteLink} testId="enable-autocomplete" onClick={this.useAutocomplete}>Turn Autocomplete On</ActionLink>
                        </GridCol>
                        :
                        <GridCol relative>
                            <AddressAutocomplete required label={autocompleteLabel} id={`${key}-address`} value={this.state.address} testId="address-autocomplete"
                                                 onChange={this.addressChanged} onBlur={this.addressBlur}
                                                 onPlaceChanged={this.placeChanged}
                                                 googleMapsApiKey={this.props.googleMapsApiKey}/>
                            <ActionLink className={styles.autocompleteLink} onClick={this.splitAddress} testId="disable-autocomplete">Turn Autocomplete Off</ActionLink>
                        </GridCol>
                    }

                    <div className="col tight vcenter">
                        <ActionLink onClick={addStop} className="mrkn med-icon fa-plus-circle"
                                    style={{marginRight: '3px'}}/>
                        <ActionLink onClick={removeStop} className="mrkn med-icon fa-times-circle-hallow"/>
                    </div>
                </GridRow>
            </TestId>
        )
    }

    addressBlur = (ev: any) => {
        // console.log('addressBlur',ev.target.value)
        this.setState({
            address: this.getAddress(),
        })
    }

    addressChanged = (ev: any) => {
        // console.log('addressChanged',ev.target.value)
        this.setState({
            address: ev.target.value,
            place_name: '',
            street: '',
            city: '',
            postal_code: '',
            province: '',
            country: '',
        })
    }

    splitAddress = () => {
        this.setState({
            splitAddress: true
        })
    }

    useAutocomplete = () => {
        this.setState({
            splitAddress: false,
            address: this.getAddress(),
        })
    }

    placeChanged = (place: any) => {
        const newValue = update(this.props.value, {
            place_name: {$set: place.placeName},
            street: {$set: place.streetAddress},
            city: {$set: place.city},
            postal_code: {$set: place.postalCode},
            province: {$set: place.province},
            country: {$set: place.countryShort},
        })
        this.props.onChange(newValue)
        // console.log('placeChanged',place,newValue,'[',formatBookingStopAddress(newValue),']')
        this.setState({
            address: formatBookingStopAddress(newValue),
        })
    }

    linkState = (name: any, valueGetter = (ev: any) => ev.target.value) => ({
        value: _.get(this.props.value, name, null),
        onChange: (...args) => {
            const value = valueGetter(...args)
            this.props.onChange(setIn(name, value, this.props.value))
        },
    })
}

interface DraggableProps extends StopProps {
    draggableId: DraggableId;
    index: number
}

export function DraggableStop({index, draggableId, ...props}: DraggableProps) {
    return (
        <Draggable draggableId={draggableId} index={index}>
            {(provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: DraggableRubric) => (
                <Stop {...props} innerRef={provided.innerRef} draggableProps={provided.draggableProps} dragHandleProps={provided.dragHandleProps} isDragging={snapshot.isDragging}/>
            )}
        </Draggable>
    )
}
